import {TENANT_ID, WORKSPACE_ID, CLIENT_USER_ID, CLIENT_USER_ID_2, CLIENT_USER_ID_3} from '../fixtures';
import {taskListUnits as baseUnits, tasks as baseTasks, taskReports as baseReports} from './retail-chain';

// Recurring-tasks history for the Meridian demo, so the Lists → Analytics tab
// has a network of store locations with ~2.5 months of results to slice.
//
// - Six extra store locations (TaskListUnits), each running the same Morning
//   Opening / Evening Closing / Weekly Deep Clean lists, but a different number
//   of checks a day (5–9) — see `extras` on StoreProfile for why that matters.
// - Generated reports for those stores *and* the two hand-authored units in
//   retail-chain.ts. Generated relative to the seed time (days before "today"),
//   deterministic per (store, task, day) so reseeding gives the same picture.
//   Hand-authored reports in retail-chain.ts always win for their day.
// - Each store has its own profile (baseline completion, weak list, weekend
//   dip, recent slump/recovery) so the heatmap and rankings tell a story.
//
// Missed occurrences are written as terminal 'skipped' reports (what the
// missing-reports cron does for tasks without late completion), never
// 'overdue' — the Tasks grid's Overdue tab keeps only its curated examples.

const NOW = new Date('2026-01-01T00:00:00.000Z').toISOString();
// History reaches back to the 1st of the month before last, so every preset
// has its comparison period covered too ("Last month" compares against the
// month before it). Every report lives in the localStorage-backed mock DB
// (~5 MB quota shared with docs), so keep the record shape lean.
const historyDays = (today: Date) => {
    const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    return Math.max(60, Math.round((today.getTime() - start.getTime()) / 86_400_000));
};
const TIMEZONE = 'America/Chicago';

type Slot = 'morning' | 'evening' | 'weekly';

interface StoreProfile {
    n: number;
    name: string;
    description: string;
    /** Typical share of tasks completed on a normal day. */
    baseline: number;
    /** Extra drop on Sat/Sun. */
    weekendDip?: number;
    /** Extra drop for one list (matched by list slot). */
    weakList?: Slot;
    weakListDip?: number;
    /** Drop over the most recent `recentDays` days (negative = recovery). */
    recentDip?: number;
    recentDays?: number;
    /** Share of misses reported as "Can't complete" instead of skipped. */
    cantShare?: number;
    /**
     * Daily checks this store runs on top of the shared routine. Stores must
     * differ in how many tasks a day they schedule: with an identical routine
     * of five, one miss is exactly one heatmap band, so every location lands on
     * the same colour on the same day and the map reads as banded columns.
     */
    extras?: {slot: 'morning' | 'evening'; name: string; photo?: boolean}[];
    /**
     * Share of today's morning list this store has already reported. Left at 0
     * the location shows the untouched live day; above 0 it shows a day in
     * progress — the demo needs both states on screen at once, so this is a
     * fixed plan rather than something driven by the wall clock.
     */
    todayProgress?: number;
    /**
     * 1-based position in the morning list of a task flagged "can't complete"
     * today, so the live day also demonstrates a reported blocker.
     */
    todayBlocker?: number;
}

const STORES: StoreProfile[] = [
    {
        n: 1, name: 'Westfield Mall', description: 'Mall store — opening and closing routines.',
        baseline: 0.97, cantShare: 0.2, todayProgress: 0.8,
        extras: [
            {slot: 'morning', name: 'Check corridor window display lighting'},
        ],
    },
    {
        n: 2, name: 'Harbor Point', description: 'Lakeshore store — evening closing has been slipping.',
        baseline: 0.86, weakList: 'evening', weakListDip: 0.3, cantShare: 0.2, todayProgress: 0.7, todayBlocker: 2,
        extras: [
            {slot: 'morning', name: 'Wipe lakeside windows and entrance mats'},
            {slot: 'evening', name: 'Reconcile card terminal batch and file the slip'},
        ],
    },
    {
        n: 3, name: 'Airport Terminal 3', description: '24/7 concourse store — stock deliveries often blocked by security.',
        baseline: 0.9, cantShare: 0.6, todayProgress: 1,
        extras: [
            {slot: 'morning', name: 'Check security seals on the stockroom door'},
            {slot: 'morning', name: 'Restock grab-and-go fridge for the first flights'},
            {slot: 'evening', name: 'Hand the shift log over to the night crew'},
            {slot: 'evening', name: 'File duty-free paperwork for the day'},
        ],
    },
    {
        n: 4, name: 'North Loop', description: 'Downtown office-district store.',
        baseline: 0.93, weekendDip: 0.12,
    },
    {
        n: 5, name: 'Riverside Plaza', description: 'New manager since last week — completion dropped.',
        baseline: 0.94, recentDip: 0.32, recentDays: 9,
        extras: [
            {slot: 'morning', name: 'Sort fitting-room returns left from yesterday'},
            {slot: 'morning', name: 'Brief the floor on today\'s promo'},
            {slot: 'evening', name: 'Log the foot-traffic counter reading'},
        ],
    },
    {
        n: 6, name: 'University District', description: 'Student-staffed store — weekends are thin, improving lately.',
        baseline: 0.72, weekendDip: 0.22, recentDip: -0.16, recentDays: 21,
        extras: [
            {slot: 'evening', name: 'Tidy study-corner seating and charging points'},
        ],
    },
];

const dailyRule = {startDate: '2026-01-01', repeatOption: 'every-day'};
const weeklyWedRule = {startDate: '2026-01-05', repeatOption: 'every-week', weekDays: [3]};
const weeklySunRule = {startDate: '2026-01-05', repeatOption: 'every-week', weekDays: [0]};

const ROUTINE: {slot: Slot; name: string; description: string; tasks: {name: string; rule: object; photo?: boolean}[]}[] = [
    {
        slot: 'morning', name: 'Morning Opening', description: 'Complete before doors open.',
        tasks: [
            {name: 'Count and record opening cash float ($300 target)', rule: dailyRule},
            {name: 'Power on all POS terminals and confirm today\'s schedule', rule: dailyRule},
            {name: 'Photo report: main floor displays before opening', rule: dailyRule, photo: true},
        ],
    },
    {
        slot: 'evening', name: 'Evening Closing', description: 'Complete before securing the store.',
        tasks: [
            {name: 'Cash count and safe drop (leave $300 float)', rule: dailyRule},
            {name: 'Photo report: stockroom tidied for next morning', rule: dailyRule, photo: true},
        ],
    },
    {
        slot: 'weekly', name: 'Weekly Deep Clean', description: 'Back-of-house deep clean.',
        tasks: [
            {name: 'Deep clean fitting rooms and mirrors', rule: weeklyWedRule},
            {name: 'Degrease staff kitchen and empty fridge', rule: weeklySunRule},
        ],
    },
];

// First 6 chars stay unique per entity (shortenId convention): thu<n>00, thl<n><k>0, th<n><k><j>0.
const unitId = (n: number) => `thu${n}00-0000-0000-0000-0000000001${n}0`;
const listId = (n: number, k: number) => `thl${n}${k}0-0000-0000-0000-0000000002${n}${k}`;
const taskId = (n: number, k: number, j: number) => `th${n}${k}${j}0-0000-0000-0000-000000000${n}${k}${j}`;

/** The shared routine plus this store's own checks for that list. */
const tasksOf = (store: StoreProfile, list: typeof ROUTINE[number]) => [
    ...list.tasks,
    ...(store.extras ?? [])
        .filter(extra => extra.slot === list.slot)
        .map(extra => ({name: extra.name, rule: dailyRule, photo: extra.photo})),
];

export const historyTaskListUnits = STORES.map(store => {
    const lists = ROUTINE.map((list, k) => ({
        id: listId(store.n, k + 1),
        name: list.name,
        description: list.description,
        childIds: tasksOf(store, list).map((_, j) => taskId(store.n, k + 1, j + 1)),
        isArchived: false, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID,
    }));
    return {
        __typename: 'TaskListUnit',
        id: unitId(store.n),
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name: store.name,
        description: store.description,
        timezone: TIMEZONE,
        logicalDayShift: 4,
        avatarPath: null,
        isArchived: false,
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        version: 1,
        createdAt: NOW,
        updatedAt: NOW,
        taskListsOrder: lists.map(l => l.id),
        taskLists: Object.fromEntries(lists.map(l => [l.id, l])),
    };
});

export const historyTasks = STORES.flatMap(store => ROUTINE.flatMap((list, k) => tasksOf(store, list).map((task, j) => ({
    __typename: 'Task',
    id: taskId(store.n, k + 1, j + 1),
    taskListUnitId: unitId(store.n),
    taskListId: listId(store.n, k + 1),
    name: task.name,
    description: '',
    scheduleRule: task.rule,
    relatedPage: null,
    config: task.photo ? {isImageRequired: true} : null,
    images: null,
    isArchived: false,
    version: 1,
    createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID,
}))));


// ── Generation ─────────────────────────────────────────────────────────────

/** Stable 0..1 hash of a string (FNV-1a → mulberry-style scramble). */
function rand(key: string): number {
    let h = 2166136261;
    for (let i = 0; i < key.length; i++) {
        h ^= key.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    h = Math.imul(h ^ (h >>> 15), 1 | h);
    h ^= h + Math.imul(h ^ (h >>> 7), 61 | h);
    return ((h ^ (h >>> 14)) >>> 0) / 4294967296;
}

const pad = (v: number) => String(v).padStart(2, '0');
const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function isScheduled(rule: {repeatOption?: string; weekDays?: number[]; startDate?: string; endDate?: string}, day: Date): boolean {
    const key = dateKey(day);
    if (rule.startDate && key < rule.startDate.slice(0, 10)) return false;
    if (rule.endDate && key > rule.endDate.slice(0, 10)) return false;
    if (rule.repeatOption === 'every-day') return true;
    if (rule.repeatOption === 'every-week') return (rule.weekDays ?? []).includes(day.getDay());
    return false;
}

const STAFF = [CLIENT_USER_ID_2, CLIENT_USER_ID_3, CLIENT_USER_ID];

const CANT_COMMENTS = [
    'POS terminal 2 would not boot — IT ticket raised.',
    'Delivery arrived late, no time before close.',
    'Safe timer locked, manager on duty did not have the code.',
    'Short-staffed shift, only one person on the floor.',
    'Cleaning supplies ran out.',
];

interface GenTask {
    id: string;
    unitId: string;
    slot: Slot;
    rule: {repeatOption?: string; weekDays?: number[]; startDate?: string; endDate?: string};
}

// Hand-authored units: map their lists onto routine slots so profiles apply.
const BASE_PROFILES: Record<string, StoreProfile> = {
    [baseUnits[0].id]: {n: 0, name: baseUnits[0].name, description: '', baseline: 0.95, weekendDip: 0.05, cantShare: 0.3, todayProgress: 0.55},
    [baseUnits[1].id]: {n: 0, name: baseUnits[1].name, description: '', baseline: 0.88, weakList: 'weekly', weakListDip: 0.2, cantShare: 0.4},
};

function baseSlot(task: {taskListUnitId: string; taskListId?: string | null}): Slot {
    const unit = baseUnits.find(u => u.id === task.taskListUnitId);
    const order = unit?.taskListsOrder ?? [];
    const idx = task.taskListId ? order.indexOf(task.taskListId) : -1;
    return idx === 0 ? 'morning' : idx === 1 ? 'evening' : 'weekly';
}

/**
 * Builds the generated report history ending at `now`'s day. Today is left
 * half-finished on purpose: the locations with a `todayProgress` have filed
 * part of their morning list, the rest have not started, so the grid and the
 * live dashboard show the "in progress" and "still open" states side by side.
 */
export function buildTaskHistoryReports(now: Date = new Date()) {
    const profiles: Record<string, StoreProfile> = {...BASE_PROFILES};
    for (const store of STORES) profiles[unitId(store.n)] = store;

    const genTasks: GenTask[] = [
        ...historyTasks.map(t => ({
            id: t.id, unitId: t.taskListUnitId, rule: t.scheduleRule as GenTask['rule'],
            slot: ROUTINE[Number(t.id[3]) - 1].slot,
        })),
        ...baseTasks
            .filter(t => !t.isArchived && profiles[t.taskListUnitId])
            .map(t => ({id: t.id, unitId: t.taskListUnitId, rule: t.scheduleRule as GenTask['rule'], slot: baseSlot(t)})),
    ];

    const authored = new Set(baseReports.map(r => `${r.taskId}|${r.logicalDayStart.slice(0, 10)}`));
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const reports: object[] = [];
    let seq = 0;

    for (let offset = historyDays(today); offset >= 0; offset--) {
        const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset);
        const key = dateKey(day);
        const weekend = day.getDay() === 0 || day.getDay() === 6;
        const dayNoonUtc = Date.parse(`${key}T12:00:00.000Z`);

        for (const task of genTasks) {
            if (!isScheduled(task.rule, day) || authored.has(`${task.id}|${key}`)) continue;
            const p = profiles[task.unitId];
            // Morning tasks close around 8–9am Chicago (13–14 UTC), evening ~5–6pm.
            const hoursAfterNoonUtc = task.slot === 'morning' ? 1 : task.slot === 'evening' ? 10 : 4;
            // Today is mid-shift: only the morning list can be reported at all,
            // and only at the locations whose `todayProgress` says they got to
            // it — plus the one task somebody flagged as blocked.
            const blockedToday = offset === 0 && p.todayBlocker != null && task.id === taskId(p.n, 1, p.todayBlocker);
            if (offset === 0 && !blockedToday && (task.slot !== 'morning' || rand(`${task.id}|today`) >= (p.todayProgress ?? 0))) {
                continue; // still open today
            }

            let chance = p.baseline;
            if (weekend && p.weekendDip) chance -= p.weekendDip;
            if (p.weakList === task.slot && p.weakListDip) chance -= p.weakListDip;
            if (p.recentDip && offset < (p.recentDays ?? 7)) chance -= p.recentDip;
            // A network-wide rough patch (holiday weekend-like) ~6 weeks ago —
            // weighted per store, since a flat dip would paint one identical column.
            if (offset >= 40 && offset <= 42) chance -= 0.06 + rand(`${task.unitId}|patch`) * 0.16;
            // Each store has its own good and bad days, so a weak day at one
            // location does not imply a weak day everywhere.
            chance += (rand(`${task.unitId}|${key}|day`) - 0.5) * 0.2;
            // And some checks are simply harder than others at that store.
            chance += (rand(`${task.id}|weight`) - 0.5) * 0.18;
            chance = blockedToday ? 0 : Math.min(0.98, Math.max(0.05, chance));

            const r = rand(`${task.id}|${key}`);
            const staff = STAFF[Math.floor(rand(`${key}|${task.unitId}|staff`) * STAFF.length)];
            const minutes = Math.floor(rand(`${task.id}|${key}|m`) * 55);
            let status: 'completed' | 'incomplete' | 'skipped';
            let completedAtMs: number | null = null;
            let comment: string | null = null;

            if (r < chance) {
                status = 'completed';
                // Never today: a late completion is stamped a day later, which
                // for the live day would land in the future.
                const late = offset > 0 && rand(`${task.id}|${key}|late`) < 0.06;
                completedAtMs = dayNoonUtc + (hoursAfterNoonUtc + (late ? 24 : 0)) * 3600_000 + minutes * 60_000;
            // On the live day a failure can only be a blocker someone flagged:
            // an occurrence nobody got to yet has no report at all (above), and
            // nothing counts as missed until the day closes.
            } else if (offset === 0 || rand(`${task.id}|${key}|cant`) < (p.cantShare ?? 0.25)) {
                status = 'incomplete';
                completedAtMs = dayNoonUtc + hoursAfterNoonUtc * 3600_000 + minutes * 60_000;
                comment = CANT_COMMENTS[Math.floor(rand(`${task.id}|${key}|c`) * CANT_COMMENTS.length)];
            } else {
                status = 'skipped';
            }

            const stamp = new Date(completedAtMs ?? dayNoonUtc + 17 * 3600_000).toISOString();
            seq += 1;
            // Lean shape: TaskReportModel defaults images/commentImages/comment.
            reports.push({
                __typename: 'TaskReport' as const,
                id: `rh${seq.toString(36).padStart(4, '0')}`,
                taskId: task.id,
                taskListUnitId: task.unitId,
                completedAt: completedAtMs != null ? stamp : null,
                logicalDayStart: `${key}T12:00:00.000Z`,
                status,
                completedBy: status === 'skipped' ? [] : [staff],
                ...(comment ? {comment} : {}),
                version: 1,
                createdAt: stamp,
                updatedAt: stamp,
            });
        }
    }
    return reports;
}
